import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Nav } from "@/components/Shell";
import { Orb, STATE_LABEL } from "@/components/Orb";
import { Composer } from "@/components/Composer";
import { MessageItem } from "@/components/MessageItem";
import { useVoice } from "@/hooks/useVoice";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, streamMessage } from "@/lib/api";

export default function Workspace() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [flash, setFlash] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const abortRef = useRef(null);
  const endRef = useRef(null);
  const prevTasks = useRef({});
  const bootRef = useRef(false);
  const spokenRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/conversations/${id}`);
      setConv(data.conversation); setMessages(data.messages); setTasks(data.tasks); setApprovals(data.approvals);
      data.tasks.forEach((t) => {
        const before = prevTasks.current[t.id];
        if (before && before !== t.status && ["completed", "failed"].includes(t.status)) {
          setFlash(t.status); setTimeout(() => setFlash(null), 2400);
          toast(t.status === "completed" ? `Research complete: ${t.title}` : `Task failed: ${t.title}`);
        }
        prevTasks.current[t.id] = t.status;
      });
    } catch (e) { setLoadError(errMsg(e)); }
  }, [id]);

  const voice = useVoice({ onTranscript: (t) => send(t, true) });

  const send = async (text, spoken = false) => {
    if (streaming) return;
    spokenRef.current = spoken;
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setStreaming(true);
    setMessages((m) => [...m, { id: "tmp-u", role: "user", content: text, channel: spoken ? "voice" : "web" }, { id: "tmp-a", role: "assistant", content: "", pending: true }]);
    let finalMsg = null;
    try {
      await streamMessage(id, { content: text, channel: spoken ? "voice" : "web" }, (ev) => {
        if (ev.type === "delta") setMessages((m) => m.map((x) => (x.id === "tmp-a" ? { ...x, content: x.content + ev.content } : x)));
        if (ev.type === "done" || ev.type === "error") finalMsg = ev.message || { status: "error", error: ev.detail, content: "" };
      }, ctrl.signal);
    } catch (e) {
      if (e.name === "AbortError") toast("Response stopped.");
      else finalMsg = { id: "tmp-a", role: "assistant", content: "", status: "error", error: e.message };
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
    await load();
    if (finalMsg?.status === "error" && finalMsg.id === "tmp-a") setMessages((m) => [...m, { ...finalMsg, id: "local-err", retryText: text }]);
    if (spoken && finalMsg?.status !== "error" && finalMsg?.content && user?.settings?.spoken_replies !== false) voice.speak(finalMsg.content);
  };

  useEffect(() => { prevTasks.current = {}; bootRef.current = false; setLoadError(null); load(); }, [load]);

  useEffect(() => {
    if (!conv || bootRef.current) return;
    bootRef.current = true;
    const { initial, voice: startVoice } = location.state || {};
    if (initial || startVoice) {
      navigate(location.pathname, { replace: true, state: null });
      if (initial) send(initial);
      else voice.start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv]);

  const active = tasks.some((t) => ["queued", "running"].includes(t.status));
  useEffect(() => {
    if (!active) return;
    const h = setInterval(load, 3000);
    return () => clearInterval(h);
  }, [active, load]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages.length, messages[messages.length - 1]?.content]);
  useEffect(() => { if (voice.error) toast.error(voice.error); }, [voice.error]);

  const taskMap = useMemo(() => Object.fromEntries(tasks.map((t) => [t.id, t])), [tasks]);
  const apMap = useMemo(() => Object.fromEntries(approvals.map((a) => [a.id, a])), [approvals]);
  const waiting = approvals.some((a) => a.status === "pending");
  const presence = voice.state === "listening" ? "listening"
    : voice.state === "transcribing" || streaming ? "thinking"
    : voice.state === "speaking" ? "speaking"
    : waiting ? "waiting" : active ? "executing" : flash || "idle";
  const lastUser = [...messages].reverse().find((m) => m.role === "user");

  if (loadError) return (
    <div className="min-h-screen"><Nav /><div className="max-w-xl mx-auto px-6 py-32" data-testid="workspace-error">
      <p className="font-display text-3xl text-ivory">This conversation could not be opened.</p>
      <p className="mt-4 text-[#8A8A8A]">{loadError}</p>
      <Link to="/conversations" className="mt-8 inline-block eyebrow !text-[#D8BFD8]">Back to conversations</Link>
    </div></div>
  );

  return (
    <div className="h-screen flex flex-col bg-[#0A0A0A]" data-testid="workspace-page">
      <Nav />
      <div className="flex-1 min-h-0 grid md:grid-cols-12">
        <aside className="md:col-span-4 lg:col-span-3 border-b md:border-b-0 md:border-r border-[#1c1c1c] flex md:flex-col items-center md:items-start gap-6 md:gap-10 px-6 md:px-10 py-5 md:py-14">
          <div className="hidden md:block"><Orb state={presence} size={170} /></div>
          <div className="md:hidden"><Orb state={presence} size={56} testId="feerd-presence-mobile" /></div>
          <div>
            <p className="eyebrow">Presence</p>
            <p className="font-display text-xl md:text-2xl text-ivory mt-1" data-testid="presence-state-label">{STATE_LABEL[presence]}</p>
            {voice.state === "speaking" && <button onClick={voice.stopSpeaking} data-testid="voice-interrupt-button" className="mt-3 text-[0.65rem] tracking-[0.2em] uppercase text-[#D8BFD8] hover:text-ivory">Interrupt</button>}
            <p className="hidden md:block mt-8 text-xs text-[#5f5f5f] leading-relaxed max-w-[15rem]">{conv?.channel === "whatsapp" ? "This thread mirrors your WhatsApp conversation." : "Voice and text share this same thread and memory."}</p>
          </div>
        </aside>
        <section className="md:col-span-8 lg:col-span-9 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto scrollbar-thin px-6 md:px-14 lg:px-20" data-testid="message-list">
            <div className="max-w-3xl pt-8 md:pt-12 pb-10">
              <h2 className="font-display text-2xl md:text-3xl text-ivory/90 tracking-tight" data-testid="conversation-title">{conv?.title || " "}</h2>
              {conv && messages.length === 0 && !streaming && (
                <p className="mt-10 text-[#5f5f5f] font-display italic text-xl" data-testid="empty-conversation">The room is quiet. Say something.</p>
              )}
              {messages.map((m) => (
                <MessageItem key={m.id} m={m} task={taskMap[m.task_id]} approval={apMap[m.approval_id]} onChange={load}
                  onSpeak={voice.speak} onRetry={m.status === "error" && !streaming && lastUser ? () => send(m.retryText || lastUser.content) : null} />
              ))}
              <div ref={endRef} />
            </div>
          </div>
          <div className="px-6 md:px-14 lg:px-20 pb-6 md:pb-8 pt-3">
            <div className="max-w-3xl">
              <Composer onSend={(t) => send(t)} onMic={voice.toggle} micState={voice.state} busy={streaming} disabled={streaming}
                onCancel={() => abortRef.current?.abort()} size="sm" autoFocus />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
